import React, { memo } from 'react';
import { Box, Flex, Text, Button, IconButton, Tooltip, useColorModeValue } from "@chakra-ui/react";
import AssistantBox from "../components/AssistantBox";
import { calcsForAnswer } from "../components/CalcCards";
import { MessageInterface } from "../configs";
import { FaRegFile, FaUndo } from "react-icons/fa";
import { LegalDraftArtifact } from "../helpers/legalArtifacts";

interface ChatMessagesProps {
  chatMessages: MessageInterface[];
  currentChat: string;
  allReferences: string[];
  apiAddress: string;
  status: string;
  onRewind?: (messageIndex: number, questionText: string) => void;
  waitAnswer?: boolean;
  canLinkCaseCitations?: boolean;
  onOpenLegalDraft?: (artifact: LegalDraftArtifact) => void;
  sourceVisibilityByMessage?: Record<number, boolean>;
  onSourceVisibilityChange?: (messageIndex: number, showSources: boolean) => void;
}

const UserBox = memo(({ message, idx, onRewind }: { message: any, idx: number, onRewind?: (messageIndex: number, questionText: string) => void }) => {
  const textColor = useColorModeValue("gray.700", "gray.200");
  const labelColor = useColorModeValue("#17404e", "#4FD1C5");
  const bubbleBg = useColorModeValue("#f7fbfc", "whiteAlpha.100");
  const bubbleBorder = useColorModeValue("rgba(23, 64, 78, 0.14)", "whiteAlpha.200");
  const bubbleShadow = useColorModeValue("0 1px 2px rgba(15, 23, 42, 0.04)", "none");
  const fileButtonBorder = useColorModeValue("gray.300", "gray.600");
  const fileButtonHover = useColorModeValue("gray.50", "gray.600");
  const timeColor = useColorModeValue("gray.400", "gray.500");

  return (
    <Flex key={`message-${idx}`} mb={6} justify="flex-end" px={{ base: 4, md: 0 }}>
      <Box
        fontFamily="Bitter, serif"
        maxW={{ base: "100%", md: "680px" }}
        textAlign="left"
        w="fit-content"
        minW={{ base: "0", md: "220px" }}
      >
        <Flex align="center" justify="flex-end" gap={1.5} mb={1}>
          {onRewind && (
            <Tooltip label="Edit from here" placement="top" hasArrow>
              <IconButton
                aria-label="Edit from here"
                icon={<FaUndo />}
                size="xs"
                variant="ghost"
                borderRadius="full"
                color={labelColor}
                minW="22px"
                h="22px"
                onClick={() => onRewind(idx, message.content[0].text)}
              />
            </Tooltip>
          )}
          <Text
            fontFamily='"Elza", sans-serif'
            fontSize="13px"
            fontWeight={700}
            color={labelColor}
            textAlign="right"
          >
            You
          </Text>
        </Flex>
        <Box
          bg={bubbleBg}
          border="1px solid"
          borderColor={bubbleBorder}
          borderRadius="8px"
          boxShadow={bubbleShadow}
          px={{ base: 4, md: 5 }}
          py={{ base: 3, md: 4 }}
        >
          <Text
            fontSize="16px"
            lineHeight="1.8"
            fontWeight={400}
            color={textColor}
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {message.content[0].text}
          </Text>
          {message.uploadedFiles && message.uploadedFiles.map((y: any, fileIdx: number) => (
            <Button
              key={`${y.id}-file-${fileIdx}`}
              size="sm"
              cursor="auto"
              variant="outline"
              leftIcon={<FaRegFile />}
              marginTop="10px"
              borderRadius="full"
              borderColor={fileButtonBorder}
              _hover={{
                bg: fileButtonHover
              }}
            >
              {y.title.split('/').pop()}
            </Button>
          ))}
        </Box>
        {message.time && (
          <Text fontSize="11px" color={timeColor} mt={1.5} textAlign="right">
            {new Date(message.time).toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Text>
        )}
      </Box>
    </Flex>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.idx === nextProps.idx &&
    prevProps.message.time === nextProps.message.time &&
    prevProps.message.content[0].text === nextProps.message.content[0].text &&
    prevProps.onRewind === nextProps.onRewind
  );
});

// Where the turn behind the answer at `idx` starts: just after the user's question (a user message of text, not of
// tool results).
const questionStart = (messages: any[], idx: number) => {
  for (let i = idx - 1; i >= 0; i--) {
    if (messages[i]?.role === 'user' && messages[i]?.content?.[0]?.type === 'text') return i + 1;
  }
  return 0;
};

const getActiveStatus = (status?: string) =>
  status && !['completed', 'canceled', 'deleted'].includes(status) ? status : undefined;

const ChatMessages: React.FC<ChatMessagesProps> = React.memo(({ status, chatMessages, currentChat, allReferences, apiAddress, onRewind, waitAnswer, canLinkCaseCitations = false, onOpenLegalDraft, sourceVisibilityByMessage = {}, onSourceVisibilityChange }) => {
  const isChatActive = !!getActiveStatus(status);
  const shouldShowPendingAssistant =
    isChatActive &&
    chatMessages.length > 0 &&
    chatMessages[chatMessages.length - 1]?.role === 'user';
  const displayMessages: any[] = shouldShowPendingAssistant
    ? [
        ...chatMessages,
        {
          role: 'assistant',
          content: [{ type: 'text', text: '' }],
          duration: 0,
          _loading: true,
          _status: status,
        },
      ]
    : chatMessages;

  return (
    <Box id="chat-container" pt={{ base: 10, md: 14 }} pb={6}>
      {displayMessages.map((x, idx) => (
        <Box key={`message-${x._rawIndex ?? idx}`} data-user-question={x.role === 'user' && x.content?.[0]?.type === 'text' ? '1' : undefined}>
          {x.role === 'assistant' && (!displayMessages[idx + 1] || displayMessages[idx + 1].content[0].type !== 'tool_result') && (
            <AssistantBox
              box={x}
              idx={x._rawIndex ?? idx}
              currentChat={currentChat}
              allReferences={allReferences}
              showThumbs={true}
              apiAddress={apiAddress}
              status={(x as any)._loading
                ? (getActiveStatus((x as any)._status) || getActiveStatus(status) || 'running')
                : (idx > displayMessages.length - 2 ? status : 'completed')}
              alternativeMessages={{ answers: {}, references: [] }}
              canLinkCaseCitations={canLinkCaseCitations}
              onOpenLegalDraft={onOpenLegalDraft}
              showSources={sourceVisibilityByMessage[x._rawIndex ?? idx] ?? true}
              onSourcesVisibilityChange={(showSources) => onSourceVisibilityChange?.(x._rawIndex ?? idx, showSources)}
              calcs={calcsForAnswer(displayMessages.slice(questionStart(displayMessages, idx), idx))}
            />
          )}
          {x.role === 'user' && x.content[0].type === 'text' && (
            <>
              {idx > 0 && (
                <Box my={8} />
              )}
              <UserBox message={x} idx={x._rawIndex ?? idx} onRewind={(waitAnswer || (status && !['completed', 'canceled'].includes(status))) ? undefined : onRewind} />
            </>
          )}
        </Box>
      ))}
    </Box>
  );
}, (prevProps, nextProps) => {
  // If currentChat changes, we need to re-render.
  if (prevProps.currentChat !== nextProps.currentChat) return false;
  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.apiAddress !== nextProps.apiAddress) return false;
  if (prevProps.canLinkCaseCitations !== nextProps.canLinkCaseCitations) return false;
  if (prevProps.onOpenLegalDraft !== nextProps.onOpenLegalDraft) return false;
  if (prevProps.onSourceVisibilityChange !== nextProps.onSourceVisibilityChange) return false;
  if (JSON.stringify(prevProps.sourceVisibilityByMessage || {}) !== JSON.stringify(nextProps.sourceVisibilityByMessage || {})) return false;
  if (prevProps.allReferences !== nextProps.allReferences && JSON.stringify(prevProps.allReferences) !== JSON.stringify(nextProps.allReferences)) return false;

  // We should only re-render if the LENGTH of chat messages changes, or if the VERY LAST message changes.
  // This is because older messages (both user and assistant) are entirely deterministic based on their index.
  // Their inner components (UserBox and AssistantBox) will handle their own memoization if they get updated props.
  if (prevProps.chatMessages.length !== nextProps.chatMessages.length) return false;

  // Check the last message since it could be streaming
  const prevLastMessage = prevProps.chatMessages[prevProps.chatMessages.length - 1];
  const nextLastMessage = nextProps.chatMessages[nextProps.chatMessages.length - 1];

  if (!prevLastMessage || !nextLastMessage) return prevLastMessage === nextLastMessage;

  // If the last message changed content (streaming)
  if (JSON.stringify(prevLastMessage) !== JSON.stringify(nextLastMessage)) return false;

  return true;
});

export default ChatMessages;
